import { writable, derived, get } from 'svelte/store';
import * as apiService from './apiService';

// Create a more robust auth store
interface AuthState {
    token: string | null;
    user: any | null;
    loading: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Declare auth store before using it
let auth: ReturnType<typeof createAuthStore>;

async function fetchCurrentUser() {
    const state = get(auth);
    if (!state.token) {
        auth.logout();
        return null;
    }

    return new Promise((resolve, reject) => {
        apiService.getCurrentUser((result, status) => {
            if (status === 200) {
                auth.setUser(result);
                resolve(result);
            } else {
                auth.logout();
                reject(new Error(result));
            }
        });
    });
}

const createAuthStore = () => {
    const initialToken = localStorage.getItem('token');
    
    const { subscribe, set, update } = writable<AuthState>({
        token: initialToken,
        user: null,
        loading: !!initialToken
    });

    const store = {
        subscribe,
        setToken: (token: string | null) => {
            if (token) {
                localStorage.setItem('token', token);
            } else {
                localStorage.removeItem('token');
            }
            update(state => ({ ...state, token }));
        },
        setUser: (user: any) => {
            update(state => ({ ...state, user, loading: false }));
        },
        logout: () => {
            localStorage.removeItem('token');
            set({ token: null, user: null, loading: false });
        },
        fetchUser: async () => {
            const state = get(auth);
            if (!state.token) {
                store.logout();
                return null;
            }

            return new Promise((resolve, reject) => {
                apiService.getCurrentUser((result, status) => {
                    if (status === 200) {
                        store.setUser(result);
                        resolve(result);
                    } else {
                        store.logout();
                        reject(new Error(result));
                    }
                });
            });
        }
    };

    return store;
};

// Initialize the auth store
auth = createAuthStore();

// If we have an initial token, fetch the user data after store creation
if (localStorage.getItem('token')) {
    fetchCurrentUser().catch(() => auth.logout());
}

export { auth };

// Derived store for loading state
export const isLoading = derived(auth, $auth => $auth.loading);

export async function requestMagicLink(email: string) {
    return new Promise((resolve, reject) => {
        apiService.requestMagicLink(email, (result, status) => {
            if (status === 200) {
                resolve(result);
            } else {
                reject(new Error(result));
            }
        });
    });
}

export async function verifyMagicLink(token: string) {
    return new Promise((resolve, reject) => {
        apiService.verifyMagicLink(token, (result, status) => {
            if (status === 200) {
                localStorage.setItem('token', result.access_token);
                auth.setToken(result.access_token);
                resolve(result);
            } else {
                reject(new Error(result));
            }
        });
    });
}

